import React, { useCallback, useMemo, useRef, useState } from "react";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { useNavigate } from "react-router-dom";
import { getApiErrorMessage } from "../../store/api/baseApi";
import {
  useCreateEmployeeMutation,
  useUploadEmployeePhotosMutation,
} from "../../store/api/employeesApi";
import "./admin.css";
import "./AddEmployee.css";

/* ─────────────────────────────────────────────────────── */
/*  Types                                                  */
/* ─────────────────────────────────────────────────────── */
type EmployeeForm = {
  firstName: string;
  lastName: string;
  gender: string;
  maritalStatus: string;
  email: string;
  phone: string;
  aadhaarNumber: string;
  department: string;
  role: string;
  status: string;
  joinedAt: string;
};

const initialForm: EmployeeForm = {
  firstName: "", lastName: "", email: "", phone: "",
  gender: "", maritalStatus: "",
  aadhaarNumber: "", department: "", role: "",
  status: "active", joinedAt: "",
};

const steps = [
  { label: "Personal Details", icon: "bi-person-vcard"  },
  { label: "Contact Details",  icon: "bi-envelope-at"   },
  { label: "Job Details",      icon: "bi-briefcase"     },
  { label: "Review",           icon: "bi-check2-circle" },
];

const departments = [
  "Civil", "Site Execution", "MEP", "Interior",
  "Procurement", "Planning", "Operations", "Admin",
];

const isValidEmail   = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
const isValidPhone   = (v: string) => { const d = v.replace(/\D/g, ""); return d.length >= 10 && d.length <= 13; };
const isValidAadhaar = (v: string) => /^\d{12}$/.test(v.replace(/\s/g, ""));

const statusBadgeClass = (s: string) =>
  s === "active" ? "text-bg-success" : s === "on-leave" ? "text-bg-warning" : "text-bg-secondary";

/* ─────────────────────────────────────────────────────── */
/*  Reusable photo-upload widget                           */
/* ─────────────────────────────────────────────────────── */
interface PhotoUploadProps {
  id: string;
  label: string;
  hint: string;
  required?: boolean;
  file: File | null;
  onChange: (f: File | null) => void;
  blurPreview?: boolean;
  touched?: boolean;
}

const PhotoUpload: React.FC<PhotoUploadProps> = ({
  id, label, hint, required, file, onChange, blurPreview, touched,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [hover, setHover]     = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const preview = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);

  const handleFile = useCallback((f: File | null) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) return;
    if (f.size > 5_000_000) { alert("File must be under 5 MB."); return; }
    onChange(f);
  }, [onChange]);

  return (
    <div className="emp-photo-field">
      <div className="emp-photo-label-row">
        <label htmlFor={id} className="emp-photo-label">
          {label} {required && <span className="text-danger">*</span>}
        </label>
        {file && (
          <button type="button" className="emp-photo-clear" onClick={() => onChange(null)}>
            <i className="bi bi-x-circle" /> Remove
          </button>
        )}
      </div>

      <div
        className={`emp-photo-drop${dragOver ? " drag-over" : ""}${file ? " has-file" : ""}${(touched && required && !file) ? " is-invalid-drop" : ""}`}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files?.[0] ?? null); }}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && inputRef.current?.click()}
        aria-label={`Upload ${label}`}
      >
        {preview ? (
          <>
            <img
              src={preview}
              alt={label}
              className="emp-photo-preview"
              style={{ filter: blurPreview && !hover ? "blur(8px)" : "none" }}
            />
            {hover && (
              <div className="emp-photo-overlay">
                <i className="bi bi-camera-fill" />
                <span>Change Photo</span>
              </div>
            )}
          </>
        ) : (
          <div className="emp-photo-placeholder">
            <i className="bi bi-cloud-arrow-up" />
            <span>Click or drag &amp; drop</span>
            <small>JPG, PNG or WebP · max 5 MB</small>
          </div>
        )}
      </div>

      {touched && required && !file && (
        <div className="emp-photo-error">
          <i className="bi bi-exclamation-triangle-fill" /> {label} is required.
        </div>
      )}

      <p className="emp-photo-hint">{hint}</p>

      <input
        ref={inputRef}
        id={id}
        type="file"
        accept="image/*"
        title={label}
        aria-label={`Upload ${label}`}
        className="emp-photo-input"
        onChange={(e) => { handleFile(e.target.files?.[0] ?? null); e.target.value = ""; }}
      />
    </div>
  );
};

/* ─────────────────────────────────────────────────────── */
/*  Main component                                         */
/* ─────────────────────────────────────────────────────── */
const AddEmployee: React.FC = () => {
  const navigate = useNavigate();
  const [createEmployee, { isLoading: isCreating }] = useCreateEmployeeMutation();
  const [uploadPhotos,   { isLoading: isUploading }] = useUploadEmployeePhotosMutation();

  const [currentStep, setCurrentStep]   = useState(0);
  const [form, setForm]                 = useState<EmployeeForm>(initialForm);
  const [profilePhoto, setProfilePhoto] = useState<File | null>(null);
  const [aadhaarPhoto, setAadhaarPhoto] = useState<File | null>(null);
  const [touched, setTouched]           = useState(false);
  const [submitError, setSubmitError]   = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");

  const isSubmitting = isCreating || isUploading;

  const updateField = (field: keyof EmployeeForm, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const stepValid = useMemo(() => {
    switch (currentStep) {
      case 0:
        // Personal details — name required, photo optional
        return form.firstName.trim().length > 0 && form.lastName.trim().length > 0;
      case 1:
        // Contact — email, phone, aadhaar number + aadhaar image required
        return (
          isValidEmail(form.email) &&
          isValidPhone(form.phone) &&
          isValidAadhaar(form.aadhaarNumber) &&
          aadhaarPhoto !== null
        );
      case 2:
        return form.department.trim().length > 0 && form.role.trim().length > 0;
      default:
        return true;
    }
  }, [currentStep, form, aadhaarPhoto]);

  const handleNext = () => {
    setTouched(true);
    if (stepValid) { setCurrentStep((p) => Math.min(steps.length - 1, p + 1)); setTouched(false); }
  };

  const handlePrevious = () => {
    setSubmitError(""); setSubmitSuccess(""); setTouched(false);
    setCurrentStep((p) => Math.max(0, p - 1));
  };

  const handleStepJump = (i: number) => {
    if (i <= currentStep) { setTouched(false); setCurrentStep(i); }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(""); setSubmitSuccess(""); setTouched(true);
    if (currentStep !== steps.length - 1) return;
    if (!stepValid) return;

    try {
      const result = await createEmployee({
        firstName:     form.firstName.trim(),
        lastName:      form.lastName.trim(),
        gender:        form.gender || null,
        maritalStatus: form.maritalStatus || null,
        email:         form.email.trim(),
        phone:         form.phone.trim(),
        department:    form.department.trim(),
        role:          form.role.trim(),
        status:        form.status || "active",
        joinedAt:      form.joinedAt || null,
        aadhaarNumber: form.aadhaarNumber.replace(/\s/g, "") || null,
      }).unwrap();

      const empId = (result as any)?.data?.id ?? (result as any)?.id;
      if (empId && (profilePhoto || aadhaarPhoto)) {
        await uploadPhotos({ id: empId, profilePhoto, aadhaarPhoto }).unwrap();
      }

      setSubmitSuccess(`${form.firstName} ${form.lastName} has been added successfully.`);
      setForm(initialForm);
      setProfilePhoto(null);
      setAadhaarPhoto(null);
      setCurrentStep(0);
      setTouched(false);
    } catch (err) {
      setSubmitError(getApiErrorMessage(err as FetchBaseQueryError));
    }
  };

  const initials = `${form.firstName.charAt(0)}${form.lastName.charAt(0)}`.toUpperCase().trim();
  const progressPercent = ((currentStep + 1) / steps.length) * 100;

  return (
    <section className="admin-page">
      <div className="container-fluid px-0">
        <div style={{ background: "#fff", borderRadius: 14, border: "1.5px solid #e2e8f0", overflow: "hidden", boxShadow: "0 2px 12px #0000000a" }}>

          {/* ── Gradient header ────────────────────── */}
          <div style={{ background: "linear-gradient(135deg, #134e4a, #0d9488, #14b8a6)", padding: "10px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 30, height: 30, borderRadius: 8, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <i className="bi bi-person-plus-fill" style={{ color: "#fff", fontSize: 14 }} />
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: "0.9rem", color: "#fff", lineHeight: 1.2 }}>Add New Employee</div>
                <div style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.75)" }}>Onboard a teammate in {steps.length} steps</div>
              </div>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
              <span style={{ background: "rgba(255,255,255,0.2)", color: "#fff", border: "1px solid rgba(255,255,255,0.35)", borderRadius: 20, padding: "3px 12px", fontSize: "0.74rem", fontWeight: 600 }}>
                Step {currentStep + 1} of {steps.length}
              </span>
              <button
                type="button"
                style={{ background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)", color: "#fff", borderRadius: 8, padding: "4px 12px", fontSize: "0.8rem", fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 5 }}
                onClick={() => navigate(-1)}
              >
                <i className="bi bi-arrow-left" /> Back
              </button>
            </div>
          </div>

          <div className="card-body p-4 p-md-5">

            {/* Alerts */}
            {submitSuccess && (
              <div className="alert alert-success d-flex align-items-center gap-2 mb-4">
                <i className="bi bi-check-circle-fill" /><span>{submitSuccess}</span>
              </div>
            )}
            {submitError && (
              <div className="alert alert-danger d-flex align-items-center gap-2 mb-4">
                <i className="bi bi-exclamation-triangle-fill" /><span>{submitError}</span>
              </div>
            )}

            {/* ── Step pills ─────────────────────────── */}
            <div className="employee-stepper mb-4">
              <div className="d-flex flex-wrap gap-2 mb-3">
                {steps.map((step, index) => {
                  const isActive    = index === currentStep;
                  const isComplete  = index < currentStep;
                  const isClickable = index <= currentStep;
                  return (
                    <button
                      key={step.label}
                      type="button"
                      className={`employee-step-pill flex-fill${isActive ? " active" : ""}${isComplete ? " complete" : ""}`}
                      onClick={() => handleStepJump(index)}
                      disabled={!isClickable}
                    >
                      <span className="employee-step-pill-index">
                        {isComplete ? <i className="bi bi-check-lg" /> : index + 1}
                      </span>
                      <span className="employee-step-pill-content">
                        <i className={`bi ${step.icon} me-2`} />{step.label}
                      </span>
                    </button>
                  );
                })}
              </div>
              <div className="progress employee-step-progress">
                <div
                  className="progress-bar progress-bar-striped progress-bar-animated"
                  style={{ width: `${progressPercent}%`, background: "linear-gradient(135deg, #0d9488, #14b8a6)" }}
                >
                  {Math.round(progressPercent)}%
                </div>
              </div>
            </div>

            <form noValidate className={touched ? "was-validated" : ""} onSubmit={handleSubmit}>

              {/* ══ Step 0: Personal Details ══════════ */}
              {currentStep === 0 && (
                <div className="row g-4">

                  {/* Profile photo — left column on md+ */}
                  <div className="col-md-3">
                    <PhotoUpload
                      id="emp-profile-photo"
                      label="Profile Photo"
                      hint="Clear face photo. Optional."
                      file={profilePhoto}
                      onChange={setProfilePhoto}
                    />
                  </div>

                  {/* Name fields — right column */}
                  <div className="col-md-9">
                    <div className="row g-3">
                      <div className="col-md-6">
                        <div style={{ borderLeft: "3px solid #6366f1", paddingLeft: 10 }}>
                          <label className="form-label fw-semibold">
                            First Name <span className="text-danger">*</span>
                            <small className="d-block fw-normal text-muted" style={{ fontSize: "0.71rem" }}>Employee's legal first name</small>
                          </label>
                          <div className="input-group has-validation">
                            <span className="input-group-text" style={{ background: "#eef2ff", border: "1.5px solid #c7d2fe", color: "#4f46e5" }}>
                              <i className="bi bi-person-fill" />
                            </span>
                            <input
                              type="text"
                              className="form-control"
                              title="First name"
                              value={form.firstName}
                              onChange={(e) => updateField("firstName", e.target.value)}
                              required
                            />
                            <div className="invalid-feedback">Please enter the first name.</div>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div style={{ borderLeft: "3px solid #0ea5e9", paddingLeft: 10 }}>
                          <label className="form-label fw-semibold">
                            Last Name <span className="text-danger">*</span>
                            <small className="d-block fw-normal text-muted" style={{ fontSize: "0.71rem" }}>Employee's legal last name</small>
                          </label>
                          <div className="input-group has-validation">
                            <span className="input-group-text" style={{ background: "#e0f2fe", border: "1.5px solid #7dd3fc", color: "#0284c7" }}>
                              <i className="bi bi-person-badge" />
                            </span>
                            <input
                              type="text"
                              className="form-control"
                              title="Last name"
                              value={form.lastName}
                              onChange={(e) => updateField("lastName", e.target.value)}
                              required
                            />
                            <div className="invalid-feedback">Please enter the last name.</div>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div style={{ borderLeft: "3px solid #ec4899", paddingLeft: 10 }}>
                          <label className="form-label fw-semibold">
                            Gender
                            <small className="d-block fw-normal text-muted" style={{ fontSize: "0.71rem" }}>Select the employee's gender</small>
                          </label>
                          <div className="input-group">
                            <span className="input-group-text" style={{ background: "#fdf2f8", border: "1.5px solid #f9a8d4", color: "#be185d" }}>
                              <i className="bi bi-person-hearts" />
                            </span>
                            <select
                              className="form-select"
                              title="Gender"
                              value={form.gender}
                              onChange={(e) => updateField("gender", e.target.value)}
                            >
                              <option value="">Select gender</option>
                              <option value="male">Male</option>
                              <option value="female">Female</option>
                              <option value="other">Other</option>
                            </select>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div style={{ borderLeft: "3px solid #8b5cf6", paddingLeft: 10 }}>
                          <label className="form-label fw-semibold">
                            Marital Status
                            <small className="d-block fw-normal text-muted" style={{ fontSize: "0.71rem" }}>Current marital status</small>
                          </label>
                          <div className="input-group">
                            <span className="input-group-text" style={{ background: "#f5f3ff", border: "1.5px solid #c4b5fd", color: "#6d28d9" }}>
                              <i className="bi bi-heart" />
                            </span>
                            <select
                              className="form-select"
                              title="Marital status"
                              value={form.maritalStatus}
                              onChange={(e) => updateField("maritalStatus", e.target.value)}
                            >
                              <option value="">Select marital status</option>
                              <option value="single">Single</option>
                              <option value="married">Married</option>
                              <option value="divorced">Divorced</option>
                              <option value="widowed">Widowed</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ══ Step 1: Contact Details ═══════════ */}
              {currentStep === 1 && (
                <div className="row g-4">

                  {/* Email + Phone */}
                  <div className="col-md-6">
                    <div style={{ borderLeft: "3px solid #f59e0b", paddingLeft: 10 }}>
                      <label className="form-label fw-semibold">
                        Email <span className="text-danger">*</span>
                        <small className="d-block fw-normal text-muted" style={{ fontSize: "0.71rem" }}>Work email for login and communication</small>
                      </label>
                      <div className="input-group has-validation">
                        <span className="input-group-text" style={{ background: "#fefce8", border: "1.5px solid #fde68a", color: "#b45309" }}>
                          <i className="bi bi-envelope" />
                        </span>
                        <input
                          type="email"
                          className={`form-control${touched && !isValidEmail(form.email) ? " is-invalid" : ""}`}
                          title="Email address"
                          value={form.email}
                          onChange={(e) => updateField("email", e.target.value)}
                          required
                        />
                        <div className="invalid-feedback">Enter a valid email address.</div>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div style={{ borderLeft: "3px solid #10b981", paddingLeft: 10 }}>
                      <label className="form-label fw-semibold">
                        Phone Number <span className="text-danger">*</span>
                        <small className="d-block fw-normal text-muted" style={{ fontSize: "0.71rem" }}>10–13 digit mobile, country code optional</small>
                      </label>
                      <div className="input-group has-validation">
                        <span className="input-group-text" style={{ background: "#d1fae5", border: "1.5px solid #6ee7b7", color: "#047857" }}>
                          <i className="bi bi-telephone" />
                        </span>
                        <input
                          type="tel"
                          className={`form-control${touched && !isValidPhone(form.phone) ? " is-invalid" : ""}`}
                          title="Phone number"
                          value={form.phone}
                          onChange={(e) => updateField("phone", e.target.value)}
                          required
                        />
                        <div className="invalid-feedback">Enter a valid 10-digit phone number.</div>
                      </div>
                    </div>
                  </div>

                  {/* Aadhaar number */}
                  <div className="col-md-6">
                    <div style={{ borderLeft: "3px solid #f43f5e", paddingLeft: 10 }}>
                      <label className="form-label fw-semibold">
                        Aadhaar Number <span className="text-danger">*</span>
                        <small className="d-block fw-normal text-muted" style={{ fontSize: "0.71rem" }}>12-digit Aadhaar card number</small>
                      </label>
                      <div className="input-group has-validation">
                        <span className="input-group-text" style={{ background: "#fff1f2", border: "1.5px solid #fda4af", color: "#be123c" }}>
                          <i className="bi bi-credit-card-2-front" />
                        </span>
                        <input
                          type="text"
                          inputMode="numeric"
                          maxLength={14}
                          className={`form-control${touched && !isValidAadhaar(form.aadhaarNumber) ? " is-invalid" : ""}`}
                          title="Aadhaar number"
                          placeholder="XXXX XXXX XXXX"
                          value={form.aadhaarNumber}
                          onChange={(e) => {
                            const raw = e.target.value.replace(/\D/g, "").slice(0, 12);
                            const fmt = raw.replace(/(\d{4})(?=\d)/g, "$1 ").trim();
                            updateField("aadhaarNumber", fmt);
                          }}
                          required
                        />
                        <div className="invalid-feedback">Enter a valid 12-digit Aadhaar number.</div>
                      </div>
                    </div>
                  </div>

                  {/* Aadhaar card image */}
                  <div className="col-md-6">
                    <PhotoUpload
                      id="emp-aadhaar-photo"
                      label="Aadhaar Card Photo"
                      hint="Upload a clear scan of both sides."
                      required
                      file={aadhaarPhoto}
                      onChange={setAadhaarPhoto}
                      blurPreview
                      touched={touched}
                    />
                  </div>
                </div>
              )}

              {/* ══ Step 2: Job Details ═══════════════ */}
              {currentStep === 2 && (
                <div className="row g-3 align-items-end">
                  <div className="col-md-6">
                    <div style={{ borderLeft: "3px solid #a855f7", paddingLeft: 10 }}>
                      <label htmlFor="empDept" className="form-label fw-semibold">
                        Department <span className="text-danger">*</span>
                        <small className="d-block fw-normal text-muted" style={{ fontSize: "0.71rem" }}>Choose or type a department</small>
                      </label>
                      <div className="input-group has-validation">
                        <span className="input-group-text" style={{ background: "#f3e8ff", border: "1.5px solid #d8b4fe", color: "#7c3aed" }}>
                          <i className="bi bi-building" />
                        </span>
                        <input id="empDept" list="employee-department-options" className="form-control" title="Department" value={form.department} onChange={(e) => updateField("department", e.target.value)} required />
                        <datalist id="employee-department-options">
                          {departments.map((d) => <option key={d} value={d} />)}
                        </datalist>
                        <div className="invalid-feedback">Choose or enter a department.</div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div style={{ borderLeft: "3px solid #3b82f6", paddingLeft: 10 }}>
                      <label htmlFor="empRole" className="form-label fw-semibold">
                        Role / Position <span className="text-danger">*</span>
                        <small className="d-block fw-normal text-muted" style={{ fontSize: "0.71rem" }}>Job title within the department</small>
                      </label>
                      <div className="input-group has-validation">
                        <span className="input-group-text" style={{ background: "#eff6ff", border: "1.5px solid #93c5fd", color: "#1d4ed8" }}>
                          <i className="bi bi-briefcase" />
                        </span>
                        <input id="empRole" type="text" className="form-control" title="Role or position" value={form.role} onChange={(e) => updateField("role", e.target.value)} required />
                        <div className="invalid-feedback">Enter a job role.</div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div style={{ borderLeft: "3px solid #f43f5e", paddingLeft: 10 }}>
                      <label htmlFor="empJoinedAt" className="form-label fw-semibold">
                        Date of Joining
                        <small className="d-block fw-normal text-muted" style={{ fontSize: "0.71rem" }}>When the employee joined</small>
                      </label>
                      <div className="input-group">
                        <span className="input-group-text" style={{ background: "#fff1f2", border: "1.5px solid #fda4af", color: "#be123c" }}>
                          <i className="bi bi-calendar-event" />
                        </span>
                        <input id="empJoinedAt" type="date" className="form-control" title="Date of joining" value={form.joinedAt} onChange={(e) => updateField("joinedAt", e.target.value)} />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div style={{ borderLeft: "3px solid #14b8a6", paddingLeft: 10 }}>
                      <label htmlFor="empStatus" className="form-label fw-semibold">
                        Status
                        <small className="d-block fw-normal text-muted" style={{ fontSize: "0.71rem" }}>Current employment status</small>
                      </label>
                      <div className="input-group">
                        <span className="input-group-text" style={{ background: "#ccfbf1", border: "1.5px solid #5eead4", color: "#0f766e" }}>
                          <i className="bi bi-check-circle" />
                        </span>
                        <select id="empStatus" className="form-select" title="Employment status" aria-label="Employment status" value={form.status} onChange={(e) => updateField("status", e.target.value)}>
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                          <option value="on-leave">On Leave</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ══ Step 3: Review ════════════════════ */}
              {currentStep === 3 && (
                <div className="row g-4 align-items-stretch">

                  {/* Avatar / photo preview */}
                  <div className="col-lg-4">
                    <div className="employee-summary-avatar">
                      {profilePhoto ? (
                        <img
                          src={URL.createObjectURL(profilePhoto)}
                          alt="Profile preview"
                          style={{ width: 88, height: 88, borderRadius: "50%", objectFit: "cover", border: "3px solid #0d9488", boxShadow: "0 4px 14px rgba(13,148,136,0.3)", marginBottom: 12 }}
                        />
                      ) : (
                        <div className="employee-avatar-circle">
                          {initials || <i className="bi bi-person" />}
                        </div>
                      )}
                      <h3 className="h5 mb-1 mt-2">
                        {form.firstName || form.lastName ? `${form.firstName} ${form.lastName}`.trim() : "New Employee"}
                      </h3>
                      <p className="text-muted mb-2">{form.role || "Role pending"}</p>
                      <span className={`badge rounded-pill px-3 py-2 ${statusBadgeClass(form.status)}`}>
                        {form.status}
                      </span>

                      {/* Document status indicators */}
                      <div className="emp-review-doc-row mt-3">
                        <div className={`emp-review-doc-item ${profilePhoto ? "ok" : "na"}`}>
                          <i className={`bi ${profilePhoto ? "bi-check-circle-fill" : "bi-dash-circle"}`} />
                          Profile Photo
                        </div>
                        <div className={`emp-review-doc-item ${aadhaarPhoto ? "ok" : "missing"}`}>
                          <i className={`bi ${aadhaarPhoto ? "bi-check-circle-fill" : "bi-exclamation-circle-fill"}`} />
                          Aadhaar Card
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Detail table */}
                  <div className="col-lg-8">
                    <div className="card border rounded-4 bg-light-subtle h-100">
                      <div className="card-body">
                        <h4 className="h6 mb-3 d-flex align-items-center gap-2">
                          <i className="bi bi-clipboard-check" /> Review Details
                        </h4>
                        <dl className="row mb-0 employee-summary-grid">
                          <dt className="col-sm-4">Full Name</dt>
                          <dd className="col-sm-8">{`${form.firstName} ${form.lastName}`.trim() || "—"}</dd>

                          <dt className="col-sm-4">Email</dt>
                          <dd className="col-sm-8">{form.email || "—"}</dd>

                          <dt className="col-sm-4">Gender</dt>
                          <dd className="col-sm-8">{form.gender || "â€”"}</dd>

                          <dt className="col-sm-4">Marital Status</dt>
                          <dd className="col-sm-8">{form.maritalStatus || "â€”"}</dd>

                          <dt className="col-sm-4">Phone</dt>
                          <dd className="col-sm-8">{form.phone || "—"}</dd>

                          <dt className="col-sm-4">Aadhaar No.</dt>
                          <dd className="col-sm-8">{form.aadhaarNumber || "—"}</dd>

                          <dt className="col-sm-4">Department</dt>
                          <dd className="col-sm-8">{form.department || "—"}</dd>

                          <dt className="col-sm-4">Role</dt>
                          <dd className="col-sm-8">{form.role || "—"}</dd>

                          <dt className="col-sm-4">Date of Joining</dt>
                          <dd className="col-sm-8">{form.joinedAt || "Not provided"}</dd>

                          <dt className="col-sm-4">Status</dt>
                          <dd className="col-sm-8">
                            <span className={`badge rounded-pill ${statusBadgeClass(form.status)}`}>
                              {form.status}
                            </span>
                          </dd>
                        </dl>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Navigation ─────────────────────────── */}
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-4 pt-3 border-top">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={handlePrevious}
                  disabled={currentStep === 0 || isSubmitting}
                >
                  <i className="bi bi-arrow-left me-1" /> Previous
                </button>

                <div className="d-flex gap-2">
                  {currentStep < steps.length - 1 ? (
                    <button
                      type="button"
                      style={{ background: "linear-gradient(135deg, #0d9488, #14b8a6)", border: "none", color: "#fff", borderRadius: 8, padding: "6px 18px", fontSize: "0.875rem", fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}
                      onClick={handleNext}
                    >
                      Next Step <i className="bi bi-arrow-right" />
                    </button>
                  ) : (
                    <button
                      type="submit"
                      style={{ background: "linear-gradient(135deg, #0d9488, #14b8a6)", border: "none", color: "#fff", borderRadius: 8, padding: "6px 18px", fontSize: "0.875rem", fontWeight: 600, cursor: isSubmitting ? "not-allowed" : "pointer", display: "inline-flex", alignItems: "center", gap: 6, opacity: isSubmitting ? 0.75 : 1 }}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" /> Saving…</>
                      ) : (
                        <><i className="bi bi-check2" /> Save Employee</>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AddEmployee;
